// src/Post.tsx
import React, { useEffect, useState, useCallback } from 'react';
import {
    Stack,
    CssBaseline,
    Typography,
    CircularProgress,
} from '@mui/material';
import Box from '@mui/joy/Box';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { PostUI } from './PostUI';
import { CenteredFab } from '../../Components/Fab/CustomButton';
import { fetchPosts } from '../../services/CommonFunctions';
import COLORS from '../../Utils/colors';


const Post: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { posts: postData, currentPage, hasMore, loading } = useSelector((state: RootState) => state.post);

    const loadMorePosts = useCallback(() => {
        if (hasMore && !loading) {
            fetchPosts(dispatch, 'image', currentPage + 1, 5, true);
        }
    }, [dispatch, currentPage, hasMore, loading]);

    const handleScroll = useCallback(() => {
        if (
            window.innerHeight + document.documentElement.scrollTop >=
            document.documentElement.offsetHeight - 1000
        ) {
            loadMorePosts();
        }
    }, [loadMorePosts]);

    useEffect(() => {
        fetchPosts(dispatch, 'image');
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // Handle scroll-to-post functionality when coming from shared link
    useEffect(() => {
        // Extract postId from URL path (e.g., /post/123456789 from https://yourapp.com/post/123456789)
        const pathSegments = location.pathname.split('/');
        const isPostRoute = pathSegments[1] === 'post';
        const postId = isPostRoute ? pathSegments[2] : pathSegments[pathSegments.length - 1];
        
        if (postId && postData.length > 0) {
            // Find the post with the matching ID
            const targetPost = postData.find(post => post._id === postId);
            if (targetPost) {
                // Scroll to the post after a short delay to ensure it's rendered
                setTimeout(() => {
                    const postElement = document.querySelector(`[data-post-id="${postId}"]`);
                    if (postElement) {
                        postElement.scrollIntoView({ 
                            behavior: 'smooth', 
                            block: 'start' 
                        });
                    }
                }, 500);
            }
        }
    }, [location.pathname, postData]);


    return (
        <>
            <CssBaseline />
            <CenteredFab
                icon={<AddIcon />}
                label="Create Post"
                onClick={() => navigate("/createpost")}
                position="end"
                sx={{
                    backgroundColor: COLORS.primary,
                    color: 'white',
                    '&:hover': {
                        backgroundColor: COLORS.burntOrange,
                    }
                }}
            />

            <Box sx={{ p: 2 }}>
                {postData.length > 0 ? (
                    <Stack spacing={4}>
                        {postData.map((item, index) => (
                            <Box key={index} data-post-id={item._id}>
                                <PostUI
                                    item={item}
                                />
                            </Box>
                        ))}
                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <CircularProgress />
                            </Box>
                        )}
                        {!hasMore && postData.length > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    No more posts to load
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                        <Typography variant="h6">No posts found</Typography>
                    </Box>
                )}
            </Box>
        </>
    );
};


export default Post;
